/**
 * useNavigatr.ts - React hook for Navigatr
 * 
 * CHANGE HISTORY:
 * - 251021_0421 [7529d5e] danielbeer: feat: migrate all packages to TypeScript structure
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { Navigatr } from './Navigatr';

/**
 * React hook for Navigatr
 */
export function useNavigatr(appName, options = {}) {
  const navigatrRef = useRef(null);
  const [, forceUpdate] = useState({});

  // Initialize Navigatr instance
  if (!navigatrRef.current) {
    navigatrRef.current = new Navigatr(appName, options);
  }

  const nav = navigatrRef.current;

  // Force re-render
  const refresh = useCallback(() => {
    forceUpdate({});
  }, []);

  const setView = useCallback((type, data) => {
    nav.setView(type, data);
    refresh();
  }, [nav, refresh]);

  const setLastUsed = useCallback((type, data) => {
    nav.setLastUsed(type, data);
    refresh();
  }, [nav, refresh]);

  const clearState = useCallback(() => {
    nav.clearState();
    refresh();
  }, [nav, refresh]);

  return {
    currentView: nav.getView(),
    setView,
    getLastUsed: (type) => nav.getLastUsed(type),
    setLastUsed,
    recentViews: nav.state.recentViews,
    getRecentViews: (type, limit) => nav.getRecentViews(type, limit),
    clearState,
    isExpired: () => nav.isExpired(),
    getState: () => nav.getState(),
    navigatr: nav
  };
}

/**
 * Hook for recent views of a specific type
 */
export function useRecentViews(appName, type, limit = null) {
  const { getRecentViews } = useNavigatr(appName);
  return getRecentViews(type, limit);
}

export default useNavigatr;
